/* TheSpidy — progressive enhancements.
   Everything here is optional: pages render and purchase without it (forms post natively). */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Cart helper: add one or more lines, then refresh Dawn's drawer ---------- */
  async function addLines(items, trigger) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.setActiveElement(trigger || document.activeElement);
    const res = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
    if (drawer) {
      // Refresh the drawer via the Section Rendering API (same HTML Dawn's own add-to-cart uses).
      const ids = drawer.getSectionsToRender().map((s) => s.id).join(',');
      const sep = window.location.search ? '&' : '?';
      const sres = await fetch(`${window.location.pathname}${window.location.search}${sep}sections=${ids}`);
      data.sections = await sres.json();
    }
    if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'spidy', cartData: data });
    }
    if (drawer) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(data);
    } else {
      window.location.href = window.routes.cart_url;
    }
    return data;
  }

  function setLoading(btn, loading) {
    if (!btn) return;
    btn.classList.toggle('is-loading', loading);
    btn.setAttribute('aria-disabled', loading ? 'true' : 'false');
    if (loading) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  /* Any form with [data-sp-add] adds the lines described by its inputs:
     items[N][id] / items[N][quantity], or a single name="id" (+ optional quantity).
     Uses delegation so it keeps working after the cart drawer re-renders. */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-sp-add]');
    if (!form) return;
    event.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    if (btn && btn.getAttribute('aria-disabled') === 'true') return;
    const errorEl = form.querySelector('[data-sp-error]');
    if (errorEl) errorEl.hidden = true;

    const fd = new FormData(form);
    const items = [];
    const indexed = {};
    for (const [key, value] of fd.entries()) {
      const m = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (m) (indexed[m[1]] = indexed[m[1]] || {})[m[2]] = Number(value);
    }
    Object.values(indexed).forEach((line) => line.id && items.push({ id: line.id, quantity: line.quantity || 1 }));
    if (!items.length && fd.get('id')) items.push({ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) });
    if (!items.length) return;

    setLoading(btn, true);
    try {
      await addLines(items, btn);
    } catch (err) {
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      }
    } finally {
      setLoading(btn, false);
    }
  });

  /* Cart add-on variant select keeps the hidden id in sync */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-sp-variant-select]');
    if (!select) return;
    const form = select.closest('form');
    const idInput = form && form.querySelector('input[name="id"]');
    if (idInput) idInput.value = select.value;
  });

  /* ---------- Set picker (PDP): radios are the product form's quantity ---------- */
  class SpSetPicker extends HTMLElement {
    connectedCallback() {
      this.slotsWrap = this.querySelector('[data-sp-slots-wrap]');
      this.addEventListener('change', (e) => {
        if (e.target.name === 'quantity') this.sync();
      });
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const html = event.data && event.data.html;
          if (html) {
            this.querySelectorAll('.sp-set-option__price[id]').forEach((el) => {
              const fresh = html.getElementById(el.id);
              if (fresh) el.innerHTML = fresh.innerHTML;
            });
          }
          // Keep the first style chooser in step with the main style picker.
          const variant = event.data && event.data.variant;
          const first = variant && this.querySelector('[data-sp-slot="1"] input[value="' + variant.id + '"]');
          if (first && !first.disabled) first.checked = true;
          this.sync();
        });
      }
      this.sync();
    }
    disconnectedCallback() {
      this.unsubscribe && this.unsubscribe();
    }
    get selected() {
      return this.querySelector('input[name="quantity"]:checked');
    }
    get quantity() {
      return this.selected ? Number(this.selected.value) : 1;
    }
    sync() {
      const input = this.selected;
      if (!input) return;
      const qty = this.quantity;
      if (this.slotsWrap) {
        this.slotsWrap.hidden = qty < 2;
        // With 2+ selected the per-unit style choosers replace the main style picker.
        const info = this.closest('.product__info-container');
        const mainPicker = info && info.querySelector('variant-selects');
        if (mainPicker) mainPicker.hidden = qty >= 2;
        this.slotsWrap.querySelectorAll('[data-sp-slot]').forEach((slot) => {
          slot.hidden = Number(slot.dataset.spSlot) > qty;
        });
      }
      const label = input.closest('.sp-set-option')?.querySelector('[data-sp-set-price]');
      document.dispatchEvent(
        new CustomEvent('sp:set-change', {
          detail: { quantity: qty, priceText: label ? label.textContent.trim() : '', title: input.dataset.title || '' },
        })
      );
    }
    /* Cart lines for a mixed-style multi-buy, or null when the normal single-variant add should run. */
    multiItems() {
      const qty = this.quantity;
      if (!this.slotsWrap || qty < 2) return null;
      const counts = new Map();
      for (let i = 1; i <= qty; i++) {
        const checked = this.slotsWrap.querySelector('[data-sp-slot="' + i + '"] input:checked');
        if (!checked) return null;
        counts.set(checked.value, (counts.get(checked.value) || 0) + 1);
      }
      return [...counts].map(([id, quantity]) => ({ id: Number(id), quantity }));
    }
  }
  if (!customElements.get('sp-set-picker')) customElements.define('sp-set-picker', SpSetPicker);

  /* ---------- Mixed-style multi-buy on the product page ----------
     Runs in the capture phase, before Dawn's product-form handler, and only when the Buy 2/3 picker
     has per-unit styles. Otherwise Dawn's normal add-to-cart runs untouched. */
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.id) return;
      const picker = document.querySelector('sp-set-picker[data-form-id="' + form.id + '"]');
      const items = picker && picker.multiItems();
      if (!items) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const btn = form.querySelector('[type="submit"]');
      if (btn && btn.getAttribute('aria-disabled') === 'true') return;
      const spinner = form.querySelector('.loading__spinner');
      const errorWrap = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      if (errorWrap) errorWrap.hidden = true;
      btn && (btn.setAttribute('aria-disabled', 'true'), btn.classList.add('loading'));
      spinner && spinner.classList.remove('hidden');
      try {
        await addLines(items, btn);
      } catch (err) {
        if (errorWrap) {
          const msg = errorWrap.querySelector('.product-form__error-message');
          if (msg) msg.textContent = err.message;
          errorWrap.hidden = false;
        }
      } finally {
        btn && (btn.removeAttribute('aria-disabled'), btn.classList.remove('loading'));
        spinner && spinner.classList.add('hidden');
      }
    },
    true
  );

  /* ---------- Sticky add to cart (appears once the main ATC scrolls out of view) ---------- */
  class SpStickyAtc extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.target = document.getElementById(this.dataset.watch);
      this.button = this.querySelector('button');
      this.priceEl = this.querySelector('[data-sp-sticky-price]');
      this.optionEl = this.querySelector('[data-sp-sticky-option]');
      if (!this.target || !this.button) return;

      this.button.addEventListener('click', () => {
        const form = document.getElementById(this.formId);
        if (!form) return;
        if (form.requestSubmit) form.requestSubmit();
        else form.querySelector('[type="submit"]')?.click();
      });

      this.observer = new IntersectionObserver(
        ([entry]) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', past);
          document.body.classList.toggle('sp-sticky-open', past);
          this.setAttribute('aria-hidden', past ? 'false' : 'true');
          this.button.tabIndex = past ? 0 : -1;
        },
        { threshold: 0 }
      );
      this.observer.observe(this.target);

      this.onSet = (e) => {
        if (this.priceEl && e.detail.priceText) this.priceEl.textContent = e.detail.priceText;
        if (this.optionEl && e.detail.title) this.optionEl.textContent = e.detail.title;
      };
      document.addEventListener('sp:set-change', this.onSet);

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event.data && event.data.variant;
          if (!variant) {
            this.button.disabled = true;
            return;
          }
          if (this.optionEl) this.optionEl.textContent = variant.title === 'Default Title' ? '' : variant.title;
          const html = event.data.html;
          const price = html && html.querySelector(`#price-${this.dataset.section} .price-item--last`);
          if (this.priceEl && price) this.priceEl.textContent = price.textContent.trim();
          this.button.disabled = !variant.available;
          const label = this.button.querySelector('.sp-btn__label');
          if (label) label.textContent = variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
        });
      }
    }
    disconnectedCallback() {
      this.observer && this.observer.disconnect();
      this.unsubscribe && this.unsubscribe();
      this.onSet && document.removeEventListener('sp:set-change', this.onSet);
    }
  }
  if (!customElements.get('sp-sticky-atc')) customElements.define('sp-sticky-atc', SpStickyAtc);

  /* ---------- Horizontal scroller with prev/next buttons ---------- */
  class SpScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.sp-scroller');
      this.prev = this.querySelector('[data-sp-prev]');
      this.next = this.querySelector('[data-sp-next]');
      if (!this.track) return;
      const step = () => (this.track.firstElementChild ? this.track.firstElementChild.getBoundingClientRect().width + 16 : 300);
      this.prev && this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      this.next && this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      };
      this.track.addEventListener('scroll', this.update, { passive: true });
      this.update();
    }
  }
  if (!customElements.get('sp-scroller')) customElements.define('sp-scroller', SpScroller);

  /* ---------- Lazy video: sources load only near the viewport; autoplay muted while visible ---------- */
  class SpVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-sp-video-toggle]');
      if (!this.video) return;
      this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion;
      this.userPaused = false;

      this.toggle &&
        this.toggle.addEventListener('click', () => {
          this.load();
          if (this.video.paused) {
            this.userPaused = false;
            this.video.play().catch(() => {});
          } else {
            this.userPaused = true;
            this.video.pause();
          }
        });
      this.video.addEventListener('play', () => this.sync());
      this.video.addEventListener('pause', () => this.sync());

      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.load();
            if (this.autoplay && !this.userPaused) this.video.play().catch(() => {});
          } else if (!this.video.paused) {
            this.video.pause();
          }
        },
        { rootMargin: '200px 0px', threshold: 0.35 }
      );
      this.observer.observe(this);
      this.sync();
    }
    load() {
      if (this.loaded) return;
      this.loaded = true;
      this.video.querySelectorAll('source[data-src]').forEach((s) => (s.src = s.dataset.src));
      this.video.load();
    }
    sync() {
      if (!this.toggle) return;
      const playing = !this.video.paused;
      this.toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      this.toggle.querySelector('[data-icon-play]')?.toggleAttribute('hidden', playing);
      this.toggle.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !playing);
    }
    disconnectedCallback() {
      this.observer && this.observer.disconnect();
    }
  }
  if (!customElements.get('sp-video')) customElements.define('sp-video', SpVideo);

  /* ---------- Click-to-load YouTube/Vimeo (no third-party JS until the viewer presses play) ---------- */
  class SpVideoEmbed extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('button');
      btn && btn.addEventListener('click', () => {
        const iframe = document.createElement('iframe');
        iframe.src = this.dataset.src;
        iframe.title = this.dataset.title || 'Video';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        this.replaceChildren(iframe);
        iframe.focus();
      }, { once: true });
    }
  }
  if (!customElements.get('sp-video-embed')) customElements.define('sp-video-embed', SpVideoEmbed);

  /* ---------- Subtle reveal on scroll ---------- */
  if (!reduceMotion && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('sp-js');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    const observe = (root) => root.querySelectorAll('.sp-reveal:not(.is-in)').forEach((el) => io.observe(el));
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => observe(document)) : observe(document);
    document.addEventListener('shopify:section:load', (e) => observe(e.target));
  }
})();
